import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, FilePlus2 } from 'lucide-react';
import {
  BoutonMint,
  BoutonPrincipal,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pagination,
  Pastille,
  PastilleSite,
} from '../components/ui/Primitives';
import { Modale } from '../components/ui/Modale';
import { BarreSites } from '../components/ui/BarreSites';
import { FicheSequence, type CibleFiche } from '../components/fiches/FicheSequence';
import {
  delaiLisible,
  demiJourneeLisible,
  heuresLisibles,
  jourLisible,
} from '../components/fiches/formats-fiche';
import { usePerimetre } from '../site/usePerimetre';
import {
  useFichesSequence,
  type EtatFiche,
  type InterventionADocumenter,
  type LigneFiche,
} from '../lib/useEspaceFormateur';
import { ErreurApi } from '../lib/api';
import { useTaillePage } from '../lib/useTaillePage';

type FiltreEtat = 'TOUTES' | EtatFiche | 'HORS_DELAI';

const FILTRES: { valeur: FiltreEtat; libelle: string }[] = [
  { valeur: 'TOUTES', libelle: 'Toutes' },
  { valeur: 'SOUMISE', libelle: 'À valider' },
  { valeur: 'HORS_DELAI', libelle: 'Hors délai' },
  { valeur: 'BROUILLON', libelle: 'Brouillons' },
  { valeur: 'RENVOYEE', libelle: 'Renvoyées' },
  { valeur: 'VALIDEE', libelle: 'Validées' },
];

/** Demi-journée sélectionnée : la ligne est relue à chaque rafraîchissement. */
type Selection = { ficheId: string | null; seanceId: string | null };

function libelleEmargement(ligne: LigneFiche | InterventionADocumenter): { texte: string; ton: string } {
  const emargement = ligne.emargement;
  if (!emargement) return { texte: 'Aucune séance', ton: 'neutral' };
  if (emargement.clos && emargement.attesteLe) return { texte: 'Clôturé · attesté', ton: 'success' };
  if (emargement.clos) return { texte: 'Clôturé · non attesté', ton: 'warning' };
  return { texte: emargement.attesteLe ? 'Ouvert · attesté' : 'Ouvert', ton: 'warning' };
}

function Delai({ ligne }: { ligne: LigneFiche | InterventionADocumenter }): JSX.Element | null {
  const texte = delaiLisible(ligne);
  if (!texte) return null;
  return (
    <span className={`block text-[10px] ${ligne.horsDelai ? 'font-semibold text-red-700' : 'text-codialis-gray'}`}>
      {texte}
    </span>
  );
}

/**
 * Écran « Fiches séquence pédagogique » (CDC §7.3).
 *
 * Le formateur rédige la fiche de chaque demi-journée animée — heures
 * réalisées, objectifs travaillés, son émargement — et la soumet ; le pôle
 * pédagogique la valide ou la renvoie, à tout moment. Délai de huit jours,
 * blocage et refus viennent de l'API : l'écran ne recalcule rien.
 */
export function FormFichesPage(): JSX.Element {
  const { site } = usePerimetre();
  const { data, isLoading, error } = useFichesSequence(site);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [choixOuvert, setChoixOuvert] = useState(false);
  const [filtre, setFiltre] = useState<FiltreEtat>('TOUTES');
  const [recherche, setRecherche] = useState('');
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();

  const estFormateur = data?.estFormateur ?? false;
  const peutValider = data?.peutValider ?? false;
  const delai = data?.delaiJours ?? 8;
  const compteurs = data?.compteurs;

  const cible: CibleFiche | null = useMemo(() => {
    if (!selection || !data) return null;
    const fiche =
      data.fiches.find((ligne) => selection.ficheId !== null && ligne.id === selection.ficheId) ??
      data.fiches.find((ligne) => selection.seanceId !== null && ligne.seanceId === selection.seanceId);
    if (fiche) return { type: 'fiche', ligne: fiche };
    const intervention = data.aDocumenter.find((ligne) => ligne.seanceId === selection.seanceId);
    return intervention ? { type: 'intervention', ligne: intervention } : null;
  }, [selection, data]);

  const filtrees = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    return (data?.fiches ?? []).filter((ligne) => {
      if (filtre === 'HORS_DELAI' && !ligne.horsDelai) return false;
      if (filtre !== 'TOUTES' && filtre !== 'HORS_DELAI' && ligne.etat !== filtre) return false;
      if (!terme) return true;
      return [ligne.titre, ligne.libelleSeance, ligne.promotion, ligne.formateur]
        .filter(Boolean)
        .some((texte) => texte!.toLowerCase().includes(terme));
    });
  }, [data, filtre, recherche]);

  const pages = Math.max(1, Math.ceil(filtrees.length / taillePage));
  const pageCourante = Math.min(page, pages);
  const visibles = filtrees.slice((pageCourante - 1) * taillePage, pageCourante * taillePage);

  const ouvrir = (ligne: LigneFiche | InterventionADocumenter) =>
    setSelection({ ficheId: 'id' in ligne ? ligne.id : null, seanceId: ligne.seanceId });

  const bandeau = data?.bandeau ?? null;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Fiches séquence pédagogique"
        sousTitre={
          estFormateur
            ? `Espace formateur · heures réalisées, émargement, validation sous ${delai} jours`
            : `Heures réalisées, émargement, validation sous ${delai} jours`
        }
        actions={
          <>
            <Link
              to="/emargement"
              className="inline-flex min-h-[44px] items-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
            >
              Feuille d’émargement
            </Link>
            {estFormateur && (
              <BoutonPrincipal onClick={() => setChoixOuvert(true)}>
                <FilePlus2 className="mr-2 h-4 w-4" aria-hidden="true" />
                Nouvelle fiche séquence
              </BoutonPrincipal>
            )}
          </>
        }
      />

      <BarreSites />

      {bandeau && (
        <div
          className={`flex flex-col items-start gap-3 rounded-2xl border px-4 py-4 sm:flex-row sm:items-center sm:px-5 ${
            bandeau.ton === 'danger' ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'
          }`}
        >
          <div
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
              bandeau.ton === 'danger' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
            }`}
          >
            <AlertTriangle className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <div className={`text-sm font-semibold ${bandeau.ton === 'danger' ? 'text-red-900' : 'text-amber-900'}`}>
              {bandeau.titre}
            </div>
            <p className={`text-xs ${bandeau.ton === 'danger' ? 'text-red-800' : 'text-amber-800'}`}>{bandeau.message}</p>
          </div>
          {bandeau.seanceId && (
            <BoutonMint onClick={() => setSelection({ ficheId: bandeau.ficheId, seanceId: bandeau.seanceId })}>
              {bandeau.ficheId ? 'Ouvrir la fiche' : 'Rédiger la fiche'}
            </BoutonMint>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle="Fiches du mois"
          valeur={compteurs?.fichesDuMois ?? '—'}
          detail={compteurs ? `${compteurs.valideesDuMois} validée(s)` : undefined}
          ton="navy"
        />
        <Compteur
          libelle="En attente de validation"
          valeur={compteurs?.aValider ?? '—'}
          detail={`délai ${delai} jours`}
          ton="warm"
        />
        <Compteur
          libelle="Heures réalisées"
          valeur={compteurs ? heuresLisibles(compteurs.heuresDuMois) : '—'}
          detail={compteurs?.mois ? `${compteurs.mois} · soumises ou validées` : undefined}
          ton="mint"
        />
        <Compteur
          libelle={estFormateur ? 'Blocage actif' : 'Hors délai'}
          valeur={compteurs?.horsDelai ?? '—'}
          detail={
            estFormateur
              ? compteurs?.horsDelai
                ? 'autres fiches bloquées'
                : 'aucune demi-journée en retard'
              : `${compteurs?.formateursBloques ?? 0} formateur(s) bloqué(s)`
          }
          ton="red"
        />
      </div>

      {error instanceof ErreurApi && <MessageErreur message={error.message} />}

      {/* La colonne latérale ne se range à côté du tableau qu'à partir de
          1280 px : en dessous, elle lui laisse la largeur de ses colonnes. */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="min-w-0 xl:col-span-2">
          <Carte
            titre={estFormateur ? 'Mes fiches séquence' : 'Fiches séquence'}
            actions={
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                <input
                  type="search"
                  value={recherche}
                  onChange={(e) => {
                    setRecherche(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Rechercher"
                  aria-label="Rechercher une fiche"
                  className="min-h-[40px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm sm:w-48"
                />
                <select
                  value={filtre}
                  onChange={(e) => {
                    setFiltre(e.target.value as FiltreEtat);
                    setPage(1);
                  }}
                  aria-label="Filtrer par état"
                  className="min-h-[40px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm sm:w-auto"
                >
                  {FILTRES.map((option) => (
                    <option key={option.valeur} value={option.valeur}>
                      {option.libelle}
                    </option>
                  ))}
                </select>
              </div>
            }
          >
            {isLoading && <Chargement />}

            {!isLoading && data && filtrees.length === 0 && (
              <div className="px-4 py-8 text-sm text-codialis-gray sm:px-6">
                {data.fiches.length === 0
                  ? estFormateur
                    ? 'Vous n’avez pas encore rédigé de fiche séquence.'
                    : 'Aucune fiche séquence n’a été rédigée sur ce périmètre.'
                  : 'Aucune fiche ne correspond à ces critères.'}
              </div>
            )}

            {visibles.length > 0 && (
              <>
                {/* Mobile : une carte par fiche. */}
                <ul className="divide-y divide-gray-100 md:hidden">
                  {visibles.map((ligne) => {
                    const emargement = libelleEmargement(ligne);
                    return (
                      <li key={ligne.id}>
                        <button
                          type="button"
                          onClick={() => ouvrir(ligne)}
                          className="w-full px-4 py-3 text-left transition hover:bg-gray-50"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="font-mono text-xs text-codialis-gray">
                                {jourLisible(ligne.date)} · {demiJourneeLisible(ligne.demiJournee)}
                              </div>
                              <div className="truncate text-sm font-semibold text-codialis-navy">
                                {ligne.titre || ligne.libelleSeance || 'Sans intitulé'}
                              </div>
                              <div className="truncate text-xs text-codialis-gray">
                                {ligne.promotion ?? '—'}
                                {!estFormateur && ` · ${ligne.formateur}`}
                              </div>
                            </div>
                            <div className="shrink-0 text-right">
                              <Pastille texte={ligne.etatLibelle} ton={ligne.etatTon} />
                              <div className="mt-1 font-mono text-xs text-codialis-navy">
                                {heuresLisibles(ligne.heuresRealisees)}
                              </div>
                            </div>
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <Pastille texte={emargement.texte} ton={emargement.ton} />
                            <Delai ligne={ligne} />
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>

                {/* Tablette et ordinateur : le tableau de la maquette. */}
                <div className="relative hidden overflow-x-auto md:block">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-codialis-gray">
                      <tr>
                        <th className="px-3 py-3 text-left font-bold">Date</th>
                        <th className="px-3 py-3 text-left font-bold">Séquence</th>
                        <th className="px-3 py-3 text-left font-bold">Heures</th>
                        <th className="px-3 py-3 text-left font-bold">Émargement</th>
                        <th className="px-3 py-3 text-left font-bold">Validation</th>
                        <th className="px-3 py-3 text-right font-bold">
                          <span className="sr-only">Action</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {visibles.map((ligne) => {
                        const emargement = libelleEmargement(ligne);
                        return (
                          <tr key={ligne.id} className="transition hover:bg-gray-50">
                            <td className="whitespace-nowrap px-3 py-3 font-mono text-xs text-codialis-gray">
                              {jourLisible(ligne.date)}
                              <span className="block text-[10px]">{demiJourneeLisible(ligne.demiJournee)}</span>
                            </td>
                            <td className="min-w-[180px] px-3 py-3">
                              <div className="font-semibold text-codialis-navy">
                                {ligne.titre || <span className="font-normal text-codialis-gray">Sans intitulé</span>}
                              </div>
                              <div className="mt-0.5 text-xs text-codialis-gray">
                                {ligne.promotion ?? '—'}
                                {!estFormateur && ` · ${ligne.formateur}`}
                                {ligne.siteCode && (
                                  <span className="ml-2 inline-block align-middle">
                                    <PastilleSite site={ligne.siteCode} />
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-3 py-3 font-mono text-xs text-codialis-navy">
                              {heuresLisibles(ligne.heuresRealisees)}
                            </td>
                            <td className="px-3 py-3">
                              <Pastille texte={emargement.texte} ton={emargement.ton} />
                            </td>
                            <td className="px-3 py-3">
                              <Pastille texte={ligne.etatLibelle} ton={ligne.etatTon} />
                              <Delai ligne={ligne} />
                            </td>
                            <td className="px-3 py-3 text-right">
                              <button
                                type="button"
                                onClick={() => ouvrir(ligne)}
                                className="min-h-[36px] text-xs font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]"
                              >
                                {peutValider && ligne.refusValidation === null ? 'Valider' : 'Ouvrir'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  page={pageCourante}
                  pages={pages}
                  total={filtrees.length}
                  affiches={visibles.length}
                  taillePage={taillePage}
                  onPage={setPage}
                  onChangerTaille={(taille) => {
                    setTaillePage(taille);
                    setPage(1);
                  }}
                  uniteLabel="fiche"
                />
              </>
            )}
          </Carte>
        </div>

        <div className="grid min-w-0 grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-1 xl:content-start">
          <Carte titre={estFormateur ? 'Demi-journées à documenter' : 'En attente de la fiche du formateur'}>
            {isLoading && <Chargement />}
            {!isLoading && data && data.aDocumenter.length === 0 && (
              <div className="px-4 py-6 text-sm text-codialis-gray sm:px-6">
                {estFormateur
                  ? 'Toutes vos demi-journées animées ont leur fiche.'
                  : 'Toutes les demi-journées animées de ce périmètre ont leur fiche.'}
              </div>
            )}
            {data?.aDocumenter.map((intervention) => (
              <div key={intervention.seanceId} className="flex items-start justify-between gap-3 px-4 py-3 sm:px-6">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-codialis-navy">{intervention.libelleSeance}</div>
                  <div className="text-[11px] text-codialis-gray">
                    {jourLisible(intervention.date)} · {demiJourneeLisible(intervention.demiJournee)}
                    {!estFormateur && ` · ${intervention.formateur}`}
                  </div>
                  <div className="text-[11px] text-codialis-gray">{intervention.prochaineEtape}</div>
                  <Delai ligne={intervention} />
                </div>
                {intervention.refusCreation === null && (
                  <button
                    type="button"
                    onClick={() => ouvrir(intervention)}
                    className="min-h-[40px] shrink-0 rounded-xl border border-gray-200 px-3 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50"
                  >
                    Rédiger
                  </button>
                )}
              </div>
            ))}
          </Carte>

          <Carte titre="Règles de validation" sansBordure>
            <ul className="space-y-2 p-4 text-xs text-codialis-gray sm:p-6">
              {[
                'Le responsable pédagogique peut valider à tout moment.',
                `Le formateur dispose de ${delai} jours, à compter de la demi-journée, pour faire valider sa fiche.`,
                'Tant que la journée n’est pas validée, le formateur est bloqué et ne peut pas soumettre d’autre fiche.',
                'La fiche part en validation une fois la feuille d’émargement clôturée et la séance attestée.',
                'Un mail est envoyé en cas de non-validation dans les délais.',
                'Un bandeau d’alerte s’affiche vers 15h30 pour rappeler les actions en attente.',
              ].map((regle) => (
                <li key={regle} className="flex items-start gap-2">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-codialis-mint" />
                  {regle}
                </li>
              ))}
            </ul>
          </Carte>
        </div>
      </div>

      {choixOuvert && data && (
        <Modale
          ouverte
          titre="Nouvelle fiche séquence"
          sousTitre="Choisissez la demi-journée que la fiche documente."
          onFermer={() => setChoixOuvert(false)}
          largeur="max-w-lg"
        >
          <div className="p-4 sm:p-6">
            {data.aDocumenter.length === 0 ? (
              <p className="text-sm text-codialis-gray">Toutes vos demi-journées animées ont déjà leur fiche.</p>
            ) : (
              <ul className="divide-y divide-gray-100 rounded-xl border border-gray-100">
                {data.aDocumenter.map((intervention) => (
                  <li key={intervention.seanceId}>
                    <button
                      type="button"
                      onClick={() => {
                        setChoixOuvert(false);
                        ouvrir(intervention);
                      }}
                      className="w-full px-4 py-3 text-left transition hover:bg-gray-50"
                    >
                      <div className="text-sm font-semibold text-codialis-navy">{intervention.libelleSeance}</div>
                      <div className="text-[11px] text-codialis-gray">
                        {jourLisible(intervention.date)} · {demiJourneeLisible(intervention.demiJournee)} ·{' '}
                        {intervention.creneau}
                      </div>
                      <Delai ligne={intervention} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Modale>
      )}

      {cible && (
        <FicheSequence
          key={cible.ligne.seanceId ?? (cible.type === 'fiche' ? cible.ligne.id : '')}
          cible={cible}
          peutValider={peutValider}
          onFermer={() => setSelection(null)}
        />
      )}
    </div>
  );
}
